import React from 'react';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { Node as PmNode } from '@atlaskit/editor-prosemirror/model';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import { ACTION } from '../../analytics';
import type { EventDispatcher } from '../../event-dispatcher';
import type { MultiBodiedExtensionActions } from '../../extensions';
import type { ChangeActiveOptions } from '../../extensions/types/extension-handler';

import { sendMBEAnalyticsEvent } from './utils';

type ActionsProps = {
	// Allows MBE macro to render bodies; see RFC: https://hello.atlassian.net/wiki/spaces/EDITOR/pages/4843571091/Editor+RFC+064+MultiBodiedExtension+Extensibility
	allowBodiedOverride: boolean;
	childrenContainer: React.ReactNode;
	editorView: EditorView;
	eventDispatcher?: EventDispatcher;
	getPos: () => number | undefined;
	node: PmNode;
	updateActiveChild: (index: number) => boolean;
};

export const useMultiBodiedExtensionActions = ({
	updateActiveChild,
	editorView,
	getPos,
	node,
	eventDispatcher,
	allowBodiedOverride,
	childrenContainer,
}: ActionsProps): MultiBodiedExtensionActions => {
	const actions: MultiBodiedExtensionActions = React.useMemo(() => {
		return {
			changeActive(index: number, options?: ChangeActiveOptions): boolean {
				const { state, dispatch } = editorView;
				const updateActiveChildResult = updateActiveChild(index);
				if (eventDispatcher) {
					sendMBEAnalyticsEvent(ACTION.CHANGE_ACTIVE, node, eventDispatcher);
				}

				const selection = options?.selection ?? 'none';
				if (selection === 'none' && !isExperimentEnabled('confluence_native_tabs_m15')) {
					return updateActiveChildResult;
				}

				const pos = getPos();
				if (typeof pos !== 'number') {
					return updateActiveChildResult;
				}
				const possiblyMbeNode = state.doc.nodeAt(pos);

				if (
					possiblyMbeNode &&
					possiblyMbeNode?.type?.name === 'multiBodiedExtension' &&
					possiblyMbeNode?.content
				) {
					if (index < 0 || index >= possiblyMbeNode?.content?.childCount) {
						throw new Error(
							`Index out of bounds: valid range is 0-${
								possiblyMbeNode?.content?.childCount - 1
							} inclusive`,
						);
					}

					let desiredPos = pos;
					for (let i = 0; i < index && i < possiblyMbeNode?.content?.childCount; i++) {
						desiredPos += possiblyMbeNode?.content?.child(i)?.nodeSize || 0;
					}

					const targetFrame = possiblyMbeNode.content.child(index);
					if (selection === 'end') {
						dispatch(
							state.tr.setSelection(
								TextSelection.near(state.doc.resolve(desiredPos + targetFrame.nodeSize), -1),
							),
						);
						editorView.focus();
					} else {
						// for 'start' and 'none' selection place a
						// collapsed cursor at the start of the target frame so the ProseMirror
						// selection always points into the active tab. This prevents stale
						// selections from persisting across tab switches (which causes paste to
						// land in the wrong tab).
						// 'none' intentionally skips editorView.focus() so DOM focus can
						// remain on the tab button for keyboard navigation.
						dispatch(state.tr.setSelection(TextSelection.near(state.doc.resolve(desiredPos + 1))));
						if (selection === 'start') {
							editorView.focus();
						}
					}
				}
				return updateActiveChildResult;
			},
			addChild(): boolean {
				const { state, dispatch } = editorView;
				const p = state.schema.nodes.paragraph.createAndFill({});
				if (!p) {
					throw new Error('Could not create paragraph');
				}

				const frame = state.schema.nodes.extensionFrame.createAndFill({}, [p]);
				const pos = getPos();
				if (typeof pos !== 'number' || !frame) {
					throw new Error('Could not create frame or position not valid');
				}

				const insertAt = Math.min((pos || 1) + node.content.size, state.doc.content.size);

				const tr = state.tr.insert(insertAt, frame);
				tr.setSelection(new TextSelection(tr.doc.resolve(insertAt + 1)));
				dispatch(tr);

				if (eventDispatcher) {
					sendMBEAnalyticsEvent(ACTION.ADD_CHILD, node, eventDispatcher);
				}
				return true;
			},
			getChildrenCount(): number {
				return node.content.childCount;
			},
			removeChild(index: number): boolean {
				const pos = getPos();
				// Ignored via go/ees007
				// eslint-disable-next-line @atlaskit/editor/enforce-todo-comment-format
				// TODO: Add child index validation here, don't trust this data
				if (typeof pos !== 'number' || typeof index !== 'number') {
					throw new Error('Position or index not valid');
				}

				const { state, dispatch } = editorView;

				if (node.content.childCount === 1) {
					const tr = state.tr;
					tr.deleteRange(pos, pos + node.content.size);
					dispatch(tr);
					return true;
				}

				const $pos = state.doc.resolve(pos);
				const $startNodePos = state.doc.resolve($pos.start($pos.depth + 1));

				const startFramePosition = $startNodePos.posAtIndex(index);
				const maybeFrameNode = state.doc.nodeAt(startFramePosition);

				if (!maybeFrameNode) {
					throw new Error('Could not find frame node');
				}
				const endFramePosition = maybeFrameNode.content.size + startFramePosition;

				const tr = state.tr;
				tr.deleteRange(startFramePosition, endFramePosition);
				dispatch(tr);
				if (eventDispatcher) {
					sendMBEAnalyticsEvent(ACTION.REMOVE_CHILD, node, eventDispatcher);
				}
				return true;
			},
			reorderChildren(fromIndex: number, toIndex: number): boolean {
				const pos = getPos();
				if (
					typeof pos !== 'number' ||
					typeof fromIndex !== 'number' ||
					typeof toIndex !== 'number'
				) {
					throw new Error('Position or index not valid');
				}
				if (fromIndex === toIndex) {
					return true;
				}
				const { state, dispatch } = editorView;

				// Use current node from state (not stale closure)
				const currentNode = state.doc.nodeAt(pos);
				if (!currentNode) {
					throw new Error('Could not find extension node');
				}

				const $pos = state.doc.resolve(pos);
				const $startNodePos = state.doc.resolve($pos.start($pos.depth + 1));

				// Collect all current frame nodes
				const frames: PmNode[] = [];
				currentNode.content.forEach((child) => {
					frames.push(child);
				});

				if (fromIndex >= frames.length || toIndex >= frames.length) {
					throw new Error('Index out of bounds');
				}

				// Reorder the frames array in memory
				const [removed] = frames.splice(fromIndex, 1);
				frames.splice(toIndex, 0, removed);

				// Atomically replace the entire MBE content with reordered frames
				const tr = state.tr;
				const containerStart = $startNodePos.pos;
				const containerEnd = containerStart + currentNode.content.size;
				tr.replaceWith(containerStart, containerEnd, frames);
				dispatch(tr);
				return true;
			},
			updateParameters(parameters, analyticsChangedParam?: string): boolean {
				const { state, dispatch } = editorView;
				const pos = getPos();
				if (typeof pos !== 'number') {
					throw new Error('Position not valid');
				}
				// We are retaining node.attrs to keep the node type and extension key
				// and only updating the parameters coming in from the user
				// parameters will contain only macroParams information
				const updatedParameters = {
					...node.attrs,
					parameters: {
						...node.attrs.parameters,
						...(expValEquals('confluence_native_tabs_experiment', 'isEnabled', true)
							? { ...parameters }
							: { macroParams: parameters }),
					},
				};

				const tr = state.tr.setNodeMarkup(pos, null, updatedParameters);
				dispatch(tr);
				if (eventDispatcher) {
					sendMBEAnalyticsEvent(
						ACTION.UPDATE_PARAMETERS,
						node,
						eventDispatcher,
						analyticsChangedParam &&
							expValEquals('confluence_native_tabs_experiment', 'isEnabled', true)
							? { changedParams: analyticsChangedParam }
							: undefined,
					);
				}
				return true;
			},
			getChildren(): Array<ADFEntity> {
				const { state } = editorView;
				const pos = getPos();
				if (typeof pos !== 'number') {
					return [];
				}
				const children = state.doc.nodeAt(pos)?.content;
				if (eventDispatcher) {
					sendMBEAnalyticsEvent(ACTION.GET_CHILDREN, node, eventDispatcher);
				}
				return children ? children.toJSON() : [];
			},
			getChildrenContainer() {
				if (!allowBodiedOverride) {
					throw new Error('Could not provide children container');
				}
				if (eventDispatcher) {
					sendMBEAnalyticsEvent(ACTION.GET_CHILDREN_CONTAINER, node, eventDispatcher);
				}

				return childrenContainer;
			},
		};
	}, [
		node,
		editorView,
		getPos,
		updateActiveChild,
		eventDispatcher,
		allowBodiedOverride,
		childrenContainer,
	]);

	return actions;
};
